/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback Control service card component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.12.0
 * @since 1.12.0
 */

import { MouseEvent, useCallback, useEffect, useState } from 'react';

import { EllipseText, LinkBlock } from '../../../../components/common';
import { IPageCashbackServiceCardTranslation } from '../../../../modules/internationalization/types/pages/cashback';
import { fonts } from '../../../../theme';
import declination from '../../../../utils/declination';
import { formatBalanceWithoutTrailingZeros } from '../../../../utils/formatText';
import { ICashbackControlService } from '../../types/cashbackControlTypes';
import { CashbackControlServiceCardStyled } from '../styled/CashbackControlStyled';
import { CashbackTextStyled } from '../styled/CashbackStyled';

export interface ICashbackControlServiceCard extends ICashbackControlService {
    active: boolean;
    cardTranslation: IPageCashbackServiceCardTranslation;
    scoreReserved?: number;
    scoreUnit?: string;

    onCardClick: () => void;
    onLinkMoreClick: () => void;
}


const CashbackControlServiceCard = (props: ICashbackControlServiceCard) => {
    const {
        cardTranslation,
        description,
        linkMore,
        scoreReserved,
        scoreUnit,
        title,

        onCardClick,
        onLinkMoreClick,
    } = props;

    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(props.active);
    }, [props.active]);

    const onCardClickHandler = useCallback(() => {
        setActive(!active);
        onCardClick();
    }, [active, onCardClick]);

    const stopClickPropagation = useCallback((e: MouseEvent) => {
        e.stopPropagation();
    }, []);

    const onLinkMoreClickHandler = useCallback((e: MouseEvent) => {
        onLinkMoreClick();
        stopClickPropagation(e);
    }, [onLinkMoreClick, stopClickPropagation]);

    return (
        <CashbackControlServiceCardStyled
            active={active}
            onClick={onCardClickHandler}
        >

            <CashbackTextStyled
                className="cashback-service-card__title"
                font={fonts.textMainBold}
            >
                {title}
            </CashbackTextStyled>

            {
                scoreUnit && scoreReserved !== undefined && (
                    <div style={{ display: 'flex' }}>
                        <CashbackTextStyled
                            className="cashback-service-card__reserved-label"
                            font={fonts.textSmall}
                        >
                            {cardTranslation.reserve}
                        </CashbackTextStyled>
                        &nbsp;
                        <CashbackTextStyled
                            className="cashback-service-card__reserved-balance"
                            font={fonts.textSmall}
                        >
                            {formatBalanceWithoutTrailingZeros(scoreReserved)}&thinsp;
                            {declination(scoreReserved, scoreUnit)}
                        </CashbackTextStyled>
                    </div>
                )
            }

            <CashbackTextStyled className="cashback-service-card__description">
                <EllipseText lines={3} title={description} onTooltipClick={stopClickPropagation}>
                    {description}
                </EllipseText>
            </CashbackTextStyled>

            {
                linkMore && (
                    <div className="cashback-service-card__link-more" onClick={onLinkMoreClickHandler}>
                        <LinkBlock isExternalUrl={true} target="_blank" url={linkMore}>
                            {cardTranslation.moreLink}
                        </LinkBlock>
                    </div>
                )
            }

        </CashbackControlServiceCardStyled>
    );
};

export default CashbackControlServiceCard;
